import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, UploadCloud, Loader2, Pencil, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { gbp, guestTierPrice } from "@/lib/pricing";
import { flavorRange } from "@/lib/flavorRanges";
import { useCategories } from "@/context/SiteContentContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const rangeLabels = (cats) => ({ dreamlabs: "DreamLabs Original", guest: cats.guest.title, economy: cats.economy.title });
const Lbl = ({ children }) => <label className="mono block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{children}</label>;
const inputCls = "mt-1 rounded-none";

export const emptyFlavorForm = {
  name: "", description: "", accent_color: "#E11D48", active: true, stock: 100, low_stock_threshold: 10, image_url: "",
  range_type: "dreamlabs", brand_name: "", guest_size_label: "", guest_price: "", guest_weight_g: 120, guest_options: [],
  guest_retail_tiers: [], guest_trade_tiers: [], enabled_strengths: ["standard", "x2", "x3"],
};
export const flavorToForm = (f) => ({
  ...emptyFlavorForm, ...f, range_type: flavorRange(f), brand_name: f.brand_name || "",
  enabled_strengths: Array.isArray(f.enabled_strengths) ? f.enabled_strengths : ["standard", "x2", "x3"],
  guest_price: f.guest_price ? String(f.guest_price) : "", guest_weight_g: f.guest_weight_g || 120,
  guest_options: (f.guest_options || []).map((o) => ({ label: o.label, price: String(o.price ?? ""), trade_price: o.trade_price == null ? "" : String(o.trade_price) })),
  guest_retail_tiers: (f.guest_retail_tiers || []).map((t) => ({ min_qty: String(t.min_qty ?? ""), price: String(t.price ?? "") })),
  guest_trade_tiers: (f.guest_trade_tiers || []).map((t) => ({ min_qty: String(t.min_qty ?? ""), price: String(t.price ?? "") })),
});
const cleanTiers = (rows) => (rows || [])
  .map((t) => ({ min_qty: parseInt(t.min_qty, 10), price: parseFloat(t.price) }))
  .filter((t) => Number.isFinite(t.min_qty) && t.min_qty >= 1 && Number.isFinite(t.price) && t.price >= 0)
  .sort((a, b) => a.min_qty - b.min_qty);
// Returns a FlavorInput payload or an error string.
export const formToPayload = (form) => {
  if (!form.name.trim()) return "Name required";
  const guest = form.range_type === "guest";
  const guestPrice = parseFloat(form.guest_price) || 0;
  if (guest && guestPrice <= 0) return "Guest products need a price — otherwise they stay hidden from the Premium range";
  // Guest details are kept even when a product is filed under DreamLabs, so switching back loses nothing.
  const options = form.guest_options.map((o) => ({ label: (o.label || "").trim(), price: parseFloat(o.price) || 0, trade_price: o.trade_price === "" || o.trade_price == null ? null : parseFloat(o.trade_price) || 0 })).filter((o) => o.label);
  return {
    name: form.name.trim(), description: form.description || "", accent_color: form.accent_color || "#E11D48",
    active: form.active !== false, stock: parseInt(form.stock, 10) || 0, low_stock_threshold: parseInt(form.low_stock_threshold, 10) || 10,
    image_url: form.image_url || "", range_type: form.range_type, brand_name: guest ? (form.brand_name || "").trim() : "",
    guest_size_label: (form.guest_size_label || "").trim(), guest_price: guestPrice,
    guest_weight_g: parseInt(form.guest_weight_g, 10) || 120, guest_options: options,
    guest_retail_tiers: cleanTiers(form.guest_retail_tiers), guest_trade_tiers: cleanTiers(form.guest_trade_tiers),
    enabled_strengths: form.range_type === "economy" ? ["standard", ...["x2", "x3"].filter((c) => (form.enabled_strengths || []).includes(c))] : null,
  };
};

const SUGGESTED_RETAIL = [{ min_qty: "1", price: "115" }, { min_qty: "2", price: "100" }, { min_qty: "5", price: "90" }, { min_qty: "10", price: "80" }];
const SUGGESTED_TRADE = [{ min_qty: "1", price: "70" }, { min_qty: "6", price: "65" }, { min_qty: "10", price: "60" }];

function TradeComparison({ retail, trade, idPrefix }) {
  const parse = (rows) => (rows || []).map((r) => ({ min_qty: parseInt(r.min_qty, 10), price: parseFloat(r.price) })).filter((r) => Number.isFinite(r.min_qty) && r.min_qty >= 1 && Number.isFinite(r.price));
  const rt = parse(retail), tt = parse(trade);
  if (!rt.length && !tt.length) return null;
  const breaks = [...new Set([...rt, ...tt].map((r) => r.min_qty))].sort((a, b) => a - b);
  return (
    <div className="border border-border bg-background p-3" data-testid={`${idPrefix}-tier-compare`}>
      <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">At a glance · retail vs trade</div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
              <th className="py-1 pr-3 text-left">Qty</th>
              <th className="py-1 pr-3 text-right">Retail</th>
              <th className="py-1 pr-3 text-right">Trade</th>
              <th className="py-1 text-right">Trade saves</th>
            </tr>
          </thead>
          <tbody>
            {breaks.map((q, i) => {
              const next = breaks[i + 1];
              const range = next ? (next - q === 1 ? `${q}` : `${q}\u2013${next - 1}`) : `${q}+`;
              const rp = rt.length ? guestTierPrice(rt, q, 0) : null;
              const tp = tt.length ? guestTierPrice(tt, q, 0) : null;
              const save = rp && tp && rp > tp ? Math.round((1 - tp / rp) * 100) : 0;
              return (
                <tr key={q} className="border-t border-border/60" data-testid={`${idPrefix}-compare-row-${q}`}>
                  <td className="py-1 pr-3 font-semibold">{range}</td>
                  <td className="py-1 pr-3 text-right mono">{rp != null ? gbp(rp) : "—"}</td>
                  <td className="py-1 pr-3 text-right mono text-primary">{tp != null ? gbp(tp) : "—"}</td>
                  <td className="py-1 text-right mono text-green-600">{save > 0 ? `${save}%` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TierEditor({ value, onChange, idPrefix, label, hint, suggest }) {
  const rows = value || [];
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid={`${idPrefix}-tiers`}>
      <div className="flex items-center justify-between">
        <Lbl>{label}</Lbl>
        {rows.length === 0 && (
          <button type="button" data-testid={`${idPrefix}-tier-suggest`} onClick={() => onChange(suggest.map((s) => ({ ...s })))} className="mono text-[10px] uppercase tracking-[0.15em] text-primary hover:underline">Use suggested tiers</button>
        )}
      </div>
      {hint && <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p>}
      <div className="mt-1 space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="mono text-[10px] text-muted-foreground">from</span>
            <Input data-testid={`${idPrefix}-tier-qty-${i}`} type="number" min="1" step="1" value={r.min_qty} onChange={(e) => set(i, "min_qty", e.target.value)} placeholder="qty" className="w-20 rounded-none mono" />
            <span className="mono text-[10px] text-muted-foreground">bottles →</span>
            <Input data-testid={`${idPrefix}-tier-price-${i}`} type="number" step="0.01" min="0" value={r.price} onChange={(e) => set(i, "price", e.target.value)} placeholder="£ each" className="w-28 rounded-none mono" />
            <button type="button" aria-label="Remove tier" data-testid={`${idPrefix}-tier-remove-${i}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1.5 text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" data-testid={`${idPrefix}-tier-add`} onClick={() => onChange([...rows, { min_qty: "", price: "" }])} className="mono inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-primary hover:underline"><Plus className="h-3 w-3" /> Add a quantity break</button>
      </div>
    </div>
  );
}

function OptionsEditor({ value, onChange, idPrefix }) {
  const rows = value || [];
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid={`${idPrefix}-options`}>
      <Lbl>Paid add-ons (optional)</Lbl>
      {rows.length > 0 && <div className="mono mt-1 flex gap-1.5 text-[9px] uppercase tracking-[0.12em] text-muted-foreground"><span className="flex-1">Add-on</span><span className="w-24">Retail £</span><span className="w-24">Trade £</span><span className="w-7" /></div>}
      <div className="mt-1 space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input data-testid={`${idPrefix}-option-label-${i}`} value={r.label} onChange={(e) => set(i, "label", e.target.value)} placeholder="e.g. Gift wrap" className="rounded-none" />
            <Input data-testid={`${idPrefix}-option-price-${i}`} type="number" step="0.01" min="0" value={r.price} onChange={(e) => set(i, "price", e.target.value)} placeholder="Retail £" className="w-24 rounded-none mono" />
            <Input data-testid={`${idPrefix}-option-trade-price-${i}`} type="number" step="0.01" min="0" value={r.trade_price ?? ""} onChange={(e) => set(i, "trade_price", e.target.value)} placeholder="= retail" title="Trade accounts pay this instead of the retail add-on price. Leave blank to charge trade the retail price." className="w-24 rounded-none mono" />
            <button type="button" aria-label="Remove add-on" data-testid={`${idPrefix}-option-remove-${i}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1.5 text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" data-testid={`${idPrefix}-option-add`} onClick={() => onChange([...rows, { label: "", price: "", trade_price: "" }])} className="mono inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-primary hover:underline"><Plus className="h-3 w-3" /> Add an add-on</button>
      </div>
    </div>
  );
}

// Shared field set for both "Add product" and the edit dialog.
export function FlavorFields({ form, setForm, idPrefix }) {
  const RANGE_LABEL = rangeLabels(useCategories());
  const guest = form.range_type === "guest";
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_170px_64px]">
        <div><Lbl>Name</Lbl><Input data-testid={`${idPrefix}-name`} value={form.name} onChange={f("name")} className={inputCls} /></div>
        <div><Lbl>Collection</Lbl>
          <select data-testid={`${idPrefix}-range`} value={form.range_type} onChange={f("range_type")} className="mt-1 h-10 w-full rounded-none border border-border bg-background px-3 text-sm">
            <option value="dreamlabs">{RANGE_LABEL.dreamlabs}</option><option value="guest">{RANGE_LABEL.guest}</option><option value="economy">{RANGE_LABEL.economy}</option>
          </select></div>
        <div><Lbl>Colour</Lbl><input type="color" data-testid={`${idPrefix}-color`} value={form.accent_color} onChange={f("accent_color")} className="mt-1 block h-10 w-full border border-border bg-transparent" /></div>
      </div>
      <div><Lbl>Description (optional)</Lbl><textarea data-testid={`${idPrefix}-description`} rows={2} value={form.description} onChange={f("description")} className="mt-1 w-full rounded-none border border-border bg-background p-2 text-sm" /></div>
      {guest && (
        <div className="space-y-3 border border-primary/30 bg-primary/5 p-3" data-testid={`${idPrefix}-guest-fields`}>
          <div className="mono text-[10px] uppercase tracking-[0.15em] text-primary">Guest product details</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Lbl>Brand (optional)</Lbl><Input data-testid={`${idPrefix}-brand`} value={form.brand_name} onChange={f("brand_name")} placeholder="Shown as a banner above the product" className={inputCls} /></div>
            <div><Lbl>Size shown to customer</Lbl><Input data-testid={`${idPrefix}-guest-size`} value={form.guest_size_label} onChange={f("guest_size_label")} placeholder="e.g. 100ml" className={inputCls} /></div>
            <div><Lbl>Price £ (required)</Lbl><Input data-testid={`${idPrefix}-guest-price`} type="number" step="0.01" min="0" value={form.guest_price} onChange={f("guest_price")} placeholder="24.99" className={`${inputCls} mono`} /></div>
            <div><Lbl>Shipping weight (g)</Lbl><Input data-testid={`${idPrefix}-guest-weight`} type="number" min="0" value={form.guest_weight_g} onChange={f("guest_weight_g")} className={`${inputCls} mono`} /></div>
          </div>
          <OptionsEditor idPrefix={idPrefix} value={form.guest_options} onChange={(v) => setForm({ ...form, guest_options: v })} />
          <div className="space-y-3 border-t border-primary/20 pt-3">
            <div className="mono text-[10px] uppercase tracking-[0.15em] text-primary">Quantity pricing (per bottle)</div>
            <p className="text-[10px] text-muted-foreground">Optional. The tier is chosen by the TOTAL number of Premium bottles in the customer's basket. Leave a ladder empty to use the flat price above.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <TierEditor idPrefix={`${idPrefix}-retail`} label="Retail prices" hint="What normal customers pay" suggest={SUGGESTED_RETAIL} value={form.guest_retail_tiers} onChange={(v) => setForm({ ...form, guest_retail_tiers: v })} />
              <TierEditor idPrefix={`${idPrefix}-trade`} label="Trade prices" hint="Trade / dropship accounts (blank = their own discount)" suggest={SUGGESTED_TRADE} value={form.guest_trade_tiers} onChange={(v) => setForm({ ...form, guest_trade_tiers: v })} />
            </div>
            <TradeComparison idPrefix={idPrefix} retail={form.guest_retail_tiers} trade={form.guest_trade_tiers} />
          </div>
        </div>
      )}
      {form.range_type === "economy" && (
        <div className="space-y-2 border border-primary/30 bg-primary/5 p-3" data-testid={`${idPrefix}-economy-strengths`}>
          <div className="mono text-[10px] uppercase tracking-[0.15em] text-primary">Strengths offered for this flavour</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2 opacity-70"><input type="checkbox" checked disabled /> Standard (always on)</label>
            {[["x2", "×2 Double Strength"], ["x3", "×3 Triple Strength"]].map(([code, label]) => (
              <label key={code} className="flex items-center gap-2">
                <input type="checkbox" data-testid={`${idPrefix}-strength-${code}`} checked={(form.enabled_strengths || []).includes(code)}
                  onChange={(e) => setForm({ ...form, enabled_strengths: ["standard", ...["x2", "x3"].filter((c) => (c === code ? e.target.checked : (form.enabled_strengths || []).includes(c)))] })} />
                {label}
              </label>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground">Untick a strength to run this flavour Standard-only (or ×2-only) while stock is tight. The Economy pricing panel's ×2/×3 switches still apply on top. Website builder, Telegram bot and checkout all follow this.</p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-[110px_130px_1fr]">
        <div><Lbl>Stock</Lbl><Input data-testid={`${idPrefix}-stock`} type="number" value={form.stock} onChange={f("stock")} className={`${inputCls} mono`} /></div>
        <div><Lbl>Low-stock alert at</Lbl><Input data-testid={`${idPrefix}-low-stock`} type="number" value={form.low_stock_threshold} onChange={f("low_stock_threshold")} className={`${inputCls} mono`} /></div>
        <label className="flex items-end gap-2 pb-2.5 text-sm"><input type="checkbox" data-testid={`${idPrefix}-active`} checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Available in store</label>
      </div>
    </div>
  );
}

function ImageUploader({ flavor, onDone }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const upload = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please drop an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      await api.put(`/admin/flavors/${flavor.id}/image`, { image_url: data.image_url });
      toast.success(`${flavor.name} image uploaded`);
      onDone(data.image_url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };
  return (
    <div data-testid={`flavor-dropzone-${flavor.id}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }} onClick={() => inputRef.current?.click()}
      className={`flex cursor-pointer items-center justify-center gap-2 border border-dashed p-3 text-center transition-colors ${dragging ? "border-foreground bg-muted" : "border-border hover:border-foreground"}`}>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`flavor-file-${flavor.id}`} onChange={(e) => upload(e.target.files)} />
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
      <span className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{uploading ? "Uploading & resizing…" : "Drag & drop or click to upload (auto-resized)"}</span>
    </div>
  );
}

function EditDialog({ flavor, onClose, onSaved }) {
  const [form, setForm] = useState(() => flavorToForm(flavor));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const payload = formToPayload(form);
    if (typeof payload === "string") return toast.error(payload);
    setSaving(true);
    try {
      await api.put(`/admin/flavors/${flavor.id}`, payload);
      toast.success(`${payload.name} saved`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-none sm:max-w-2xl" data-testid="flavor-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit {flavor.name}</DialogTitle>
          <DialogDescription>Everything about this product — name, collection, price, add-ons, stock and image — in one place.</DialogDescription>
        </DialogHeader>
        <FlavorFields form={form} setForm={setForm} idPrefix="edit" />
        <div className="grid gap-3 sm:grid-cols-[96px_1fr]">
          <div className="h-24 w-24 overflow-hidden border border-border bg-white">
            {form.image_url ? <img src={form.image_url} alt="" className="h-full w-full object-contain" /> : <span className="block h-full w-full" style={{ background: form.accent_color }} />}
          </div>
          <div className="space-y-2">
            <div><Lbl>Image URL</Lbl><Input data-testid="edit-image-url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="or paste an image URL" className={`${inputCls} mono text-xs`} /></div>
            <ImageUploader flavor={flavor} onDone={(url) => { setForm((c) => ({ ...c, image_url: url })); onSaved(false); }} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-none" data-testid="edit-save"><Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FlavorCard({ f, onEdit, onDelete, onReload, waiting = 0 }) {
  const RANGE_LABEL = rangeLabels(useCategories());
  const [stock, setStock] = useState(String(f.stock));
  useEffect(() => { setStock(String(f.stock)); }, [f.stock]);
  const out = f.stock <= 0;
  const low = !out && f.stock <= (f.low_stock_threshold ?? 10);
  const guest = flavorRange(f) === "guest";
  const hidden = guest && !(Number(f.guest_price) > 0);
  const saveStock = async () => {
    await api.put(`/admin/flavors/${f.id}/stock`, { stock: parseInt(stock, 10) || 0, low_stock_threshold: f.low_stock_threshold ?? 10 });
    onReload(); toast.success(`${f.name} stock updated`);
  };
  const toggleActive = async (active) => {
    try {
      await api.patch(`/admin/flavors/${f.id}/active`, { active });
      onReload(); toast.success(active ? `${f.name} is back in the store` : `${f.name} paused — hidden from customers`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className={`flex flex-col bg-card p-4 ${f.active === false ? "opacity-70" : ""}`} data-testid={`admin-flavor-${f.id}`}>
      <div className="flex items-start gap-3">
        {f.image_url ? (
          <span className="h-16 w-12 shrink-0 overflow-hidden border border-border bg-white" style={{ opacity: out ? 0.4 : 1 }}><img src={f.image_url} alt="" className="h-full w-full object-cover" /></span>
        ) : (
          <span className="h-16 w-12 shrink-0" style={{ background: f.accent_color, opacity: out ? 0.3 : 1 }} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="truncate font-medium">{f.name}</div>
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-[11px] font-medium" title={f.active === false ? "Paused — customers can't see or buy it" : "Live in the store"}>
              <input type="checkbox" data-testid={`flavor-active-toggle-${f.id}`} checked={f.active !== false} onChange={(e) => toggleActive(e.target.checked)} />
              {f.active === false ? <span className="text-muted-foreground">Off</span> : <span className="text-primary">On</span>}
            </label>
          </div>
          <div className="mono mt-0.5 truncate text-[9px] uppercase tracking-[0.12em] text-primary">{guest ? `Guest${f.brand_name ? ` · ${f.brand_name}` : ""}` : flavorRange(f) === "economy" ? RANGE_LABEL.economy : RANGE_LABEL.dreamlabs}</div>
          {guest && <div className="mono mt-0.5 text-[10px] text-muted-foreground" data-testid={`flavor-guest-summary-${f.id}`}>{f.guest_size_label || "No size"} · {Number(f.guest_price) > 0 ? gbp(f.guest_price) : "no price"}{f.guest_options?.length ? ` · ${f.guest_options.length} add-on${f.guest_options.length > 1 ? "s" : ""}` : ""}</div>}
          <div className="mt-1 flex flex-wrap gap-1">
            {hidden && <span className="mono inline-block bg-destructive px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white" data-testid={`flavor-guest-hidden-${f.id}`}>Hidden — set a price</span>}
            {f.active === false && <span className="mono inline-block border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em] text-muted-foreground" data-testid={`flavor-inactive-${f.id}`}>Paused · not in store</span>}
            <span className={`mono inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em] ${out ? "bg-destructive text-white" : low ? "bg-yellow-500/20 text-yellow-600" : "border border-border text-muted-foreground"}`}>
              {out ? "Out of stock" : low ? `Low · ${f.stock} left` : `${f.stock} in stock`}
            </span>
            {waiting > 0 && <span className="mono inline-block border border-primary bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-primary" data-testid={`flavor-restock-waiting-${f.id}`}>{waiting} waiting for restock</span>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        <span className="mono shrink-0 text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Stock</span>
        <input data-testid={`flavor-stock-edit-${f.id}`} type="number" value={stock} onChange={(e) => setStock(e.target.value)} className="w-20 rounded-none border border-border bg-background p-1.5 text-xs mono" />
        <button aria-label="Save stock" data-testid={`flavor-stock-save-${f.id}`} onClick={saveStock} className="border border-border p-1.5 hover:bg-muted"><Save className="h-3.5 w-3.5" /></button>
        <span className="flex-1" />
        <button data-testid={`flavor-edit-${f.id}`} onClick={onEdit} className="inline-flex items-center gap-1.5 border border-foreground px-3 py-1.5 text-xs font-medium transition-colors hover:bg-foreground hover:text-background"><Pencil className="h-3.5 w-3.5" /> Edit</button>
        <button aria-label={`Delete ${f.name}`} data-testid={`flavor-delete-${f.id}`} onClick={onDelete} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function FlavorsAdmin() {
  const RANGE_LABEL = rangeLabels(useCategories());
  const [flavors, setFlavors] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyFlavorForm);
  const [editing, setEditing] = useState(null);
  const [waiting, setWaiting] = useState({});
  const editParamUsed = React.useRef(false);
  const load = () => api.get("/flavors?all=true").then((r) => setFlavors(r.data));
  useEffect(() => { load(); api.get("/admin/restock-subscribers").then((r) => setWaiting(r.data || {})).catch(() => {}); }, []);

  // Open a product straight from a Telegram "Restock now" deep-link (/admin?tab=flavors&edit=<id>).
  // Only once — later reloads (e.g. after saving stock) must not re-open the dialog.
  useEffect(() => {
    if (editParamUsed.current) return;
    const id = new URLSearchParams(window.location.search).get("edit");
    if (id && flavors.some((f) => f.id === id)) { editParamUsed.current = true; setEditing(id); }
  }, [flavors]);

  const counts = useMemo(() => flavors.reduce((a, f) => { a[flavorRange(f)] = (a[flavorRange(f)] || 0) + 1; return a; }, {}), [flavors]);
  const visible = useMemo(() => (filter === "all" ? flavors : flavors.filter((f) => flavorRange(f) === filter)), [flavors, filter]);
  const hiddenGuests = flavors.filter((f) => flavorRange(f) === "guest" && !(Number(f.guest_price) > 0)).length;

  const create = async () => {
    const payload = formToPayload(form);
    if (typeof payload === "string") return toast.error(payload);
    try {
      await api.post("/admin/flavors", payload);
      setForm({ ...emptyFlavorForm, range_type: form.range_type });
      setShowAdd(false);
      load();
      toast.success(`${payload.name} added to ${RANGE_LABEL[payload.range_type]}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.name}? This cannot be undone.`)) return;
    await api.delete(`/admin/flavors/${f.id}`); load(); toast.success("Deleted");
  };
  const editingFlavor = editing ? flavors.find((f) => f.id === editing) : null;

  return (
    <div className="mt-6" data-testid="flavors-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Products &amp; Stock</h2>
          <p className="text-xs text-muted-foreground">{counts.dreamlabs || 0} DreamLabs · {counts.guest || 0} Guest · {counts.economy || 0} Economy{hiddenGuests ? <span className="text-destructive"> · {hiddenGuests} guest product{hiddenGuests > 1 ? "s" : ""} hidden (no price)</span> : null}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="flavor-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 rounded-none border border-border bg-background px-3 text-sm">
            <option value="all">{`Show: all collections (${flavors.length})`}</option>
            <option value="dreamlabs">{`Show: DreamLabs Original (${counts.dreamlabs || 0})`}</option>
            <option value="guest">{`Show: ${RANGE_LABEL.guest} (${counts.guest || 0})`}</option>
            <option value="economy">{`Show: ${RANGE_LABEL.economy} (${counts.economy || 0})`}</option>
          </select>
          <Button data-testid="flavor-add-toggle" onClick={() => { setShowAdd((v) => !v); if (filter !== "all") setForm((c) => ({ ...c, range_type: filter })); }} className="rounded-none"><Plus className="mr-1.5 h-4 w-4" /> {showAdd ? "Close" : "Add product"}</Button>
        </div>
      </div>

      {showAdd && (
        <div className="mt-4 border border-border p-4" data-testid="flavor-form">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">New product</div>
          <div className="mt-3"><FlavorFields form={form} setForm={setForm} idPrefix="flavor" /></div>
          <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-none">Cancel</Button>
            <Button data-testid="flavor-add-btn" onClick={create} className="rounded-none"><Plus className="mr-1.5 h-4 w-4" /> Add to {RANGE_LABEL[form.range_type]}</Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">You can upload the product image from the Edit button once it's created — images are resized automatically to match the rest of the range.</p>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="mt-4 border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="flavor-empty">No products in this collection yet.</div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((f) => <FlavorCard key={f.id} f={f} waiting={waiting[f.id] || 0} onEdit={() => setEditing(f.id)} onDelete={() => remove(f)} onReload={load} />)}
        </div>
      )}
      {editingFlavor && <EditDialog key={editingFlavor.id} flavor={editingFlavor} onClose={() => setEditing(null)} onSaved={(close = true) => { load(); if (close) setEditing(null); }} />}
    </div>
  );
}
